<script lang="ts">
    import { getContainer } from '$components/Context/ContainerContext.js';
    import Spinner, { type SpinnerSize } from '$components/Spinner/Spinner.svelte';
    import {
        FollowableQueryDocument,
        type FollowButtonFragment,
        FollowButtonFragmentDoc,
        ToggleFollowingMutationDocument,
    } from '$gql/graphql.js';
    import { cx } from '$lib/utils/cx.js';
    import { temporizePromise } from '$lib/utils/temporizePromise.js';
    import { match } from 'ts-pattern';
    import { query } from '$lib/apollo/SvelteApollo';
    import { useFragment } from '$gql';
    import SecuredArea from '$components/SecuredArea/SecuredArea.svelte';
    import * as t from '$trans';

    export let target: FollowButtonFragment;
    export let loading: boolean = false;

    let className = '';
    export { className as class };

    export let followedClassName = '';
    export let unfollowedClassName = '';
    export let spinnerClass = '';
    export let spinnerSize: SpinnerSize = 'xs';

    const { apollo, lang } = getContainer();

    let toggling = false;

    function toggle() {
        toggling = true;
        return temporizePromise(
            500,
            (async () => {
                await apollo().mutate({
                    mutation: ToggleFollowingMutationDocument,
                    variables: {
                        target: targetToFollowableInput(target),
                    },
                });
            })(),
        ).finally(() => (toggling = false));
    }

    function targetToFollowableInput(target: FollowButtonFragment) {
        return match(target)
            .with({ __typename: 'Company' }, ({ id }) => ({
                companyId: id,
            }))
            .with({ __typename: 'User' }, ({ id }) => ({
                userId: id,
            }))
            .exhaustive();
    }

    const { result, setVariables } = query(FollowableQueryDocument, {
        variables: { id: targetToFollowableInput(target) },
    });
    $: setVariables({ id: targetToFollowableInput(target) });
    $: followedByMe =
        useFragment(FollowButtonFragmentDoc, $result.data?.followable)?.followedByMe ??
        target.followedByMe;
</script>

<SecuredArea>
    <slot
        {followedByMe}
        {toggling}
        loading={loading || $result.loading}
        {toggle}
        disabled={loading || toggling}
    >
        <button
            class={cx(
                'group',
                className,
                followedByMe ? followedClassName : unfollowedClassName,
            )}
            data-followed={followedByMe ? true : undefined}
            data-not-followed={!followedByMe ? true : undefined}
            on:click={() => toggle()}
            disabled={loading || toggling}
        >
            <span class="flex items-center gap-x-2">
                {#if followedByMe}
                    {#if loading || toggling}
                        <Spinner size={spinnerSize} class={spinnerClass} />
                    {:else}
                        <i class={'fa fa-check'} aria-hidden={true} />
                    {/if}

                    <span>{t.ui.followed({}, $lang)}</span>
                {:else}
                    {#if loading || toggling}
                        <Spinner size={spinnerSize} class={spinnerClass} />
                    {:else}
                        <i class={'tnp tnp-follow'} aria-hidden={true} />
                    {/if}

                    <span>{t.ui.follow({}, $lang)}</span>
                {/if}
            </span>
        </button>
    </slot>
</SecuredArea>
